import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { maskUsername } from "../../data/usernamePool";
// Canonical tactile-gold recipe (gradient + inset highlights + drop shadow)
// used by the "BIG WIN" badge so it stays in lockstep with the gold button
// and header avatar. Vite dedupes the shared import. See button-gold.css.
import "../../../components/ui/button-gold.css";

// Thumbnail with skeleton fallback. Image is rendered hidden until it
// loads; if it errors out (404 / network failure), the skeleton stays
// up forever instead of showing a broken-image icon. Resets to loading
// when `src` changes so cycling through a feed works correctly.
const Thumbnail = ({ src, alt, isBigWin }) => {
  const imgRef = useRef(null);
  const [status, setStatus] = useState(src ? "loading" : "error");

  useEffect(() => {
    setStatus(src ? "loading" : "error");
  }, [src]);

  // Cached-image short-circuit. SocialProofSection preloads each new
  // card's thumbnail before committing the card to state, so by the time
  // we mount the <img> element the file is already in the browser cache
  // and `complete` is synchronously true. Without this hook the parent
  // <img>'s onLoad would still fire one frame later, so React would paint
  // the skeleton for one frame before flipping to 'loaded' — which is the
  // skeleton-flash users see during the live ticker's 350ms slide-in.
  // useLayoutEffect runs post-commit / pre-paint, so flipping status here
  // applies before the browser draws anything.
  useLayoutEffect(() => {
    const el = imgRef.current;
    if (el && el.complete && el.naturalHeight > 0) {
      setStatus("loaded");
    }
  }, [src]);

  const showImage = status === "loaded";
  const showSkeleton = !showImage; // covers loading and error

  const skeletonOverlay = (
    <div className="absolute inset-0 bg-card animate-pulse" />
  );

  const imageEl = src && (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      onLoad={() => setStatus("loaded")}
      onError={() => setStatus("error")}
      className={`w-full h-full object-cover transition-opacity duration-200 ${
        showImage ? "opacity-100" : "opacity-0"
      }`}
    />
  );

  if (isBigWin) {
    return (
      <div
        className="w-14 h-14 rounded-lg flex-shrink-0 p-[1px]"
        style={{
          background:
            "linear-gradient(135deg, #FFE48A 0%, #F5B955 50%, #A87A1F 100%)",
        }}
      >
        <div className="relative w-full h-full rounded-[7px] overflow-hidden bg-background">
          {showSkeleton && skeletonOverlay}
          {imageEl}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-background">
      {showSkeleton && skeletonOverlay}
      {imageEl}
    </div>
  );
};

// "Big win" thresholds. Gold Coins are nominal play money; Cluck Bucks
// are ~$1 each, so the BIG WIN bar is set higher in real-cash terms.
// "Thousands for Gold Coins, hundreds for Cluck Bucks."
const BIG_WIN_THRESHOLD = {
  gold: 1000,
  cluck: 100,
};

// Per-outcome card backgrounds.
const CARD_BACKGROUNDS = {
  bigWin: "/big-win.png",
  win: "/win.png",
  loss: "/loss.png",
};

const SocialProofCard = ({
  game,
  user,
  amount,
  currency, // 'gold' or 'cluck'
  isWin = true,
  timestamp,
  isMe = false,
  onClick,
  className = "",
}) => {
  const isInteractive = typeof onClick === "function";
  const formatAmount = (n) =>
    Math.abs(n).toLocaleString("en-US", {
      minimumFractionDigits: currency === "cluck" ? 2 : 0,
      maximumFractionDigits: currency === "cluck" ? 2 : 0,
    });

  const isBigWin =
    isWin && Math.abs(amount) >= (BIG_WIN_THRESHOLD[currency] ?? Infinity);

  // All four badges share the same metallic-pill recipe: 3-stop vertical
  // gradient (light top → mid → dark bottom) with a tinted inset highlight
  // on the top edge and a tinted inset shadow on the bottom edge, plus a
  // soft outer drop shadow for separation. Text colour is the darkest hue
  // of each gradient so the type tints match the pill.
  //
  // The YOU badge takes precedence over BIG WIN / WIN / LOSS — when the
  // current user is the player, every row is labelled "YOU" regardless of
  // outcome bucket. (LOSS shouldn't actually reach this branch since the
  // /activity/me/recent endpoint is wins-only, but the precedence rule
  // keeps the badge consistent if that contract ever changes.)
  const badge = isMe
    ? {
        label: "YOU",
        className:
          "bg-gradient-to-b from-[#FCD9B6] via-[#F97316] to-[#9A3412] text-[#3A1605] shadow-[inset_0_1px_0_rgba(255,225,200,0.75),inset_0_-1px_0_rgba(120,50,15,0.45),0_1px_2px_rgba(0,0,0,0.35)]",
      }
    : !isWin
      ? {
          label: "LOSS",
          className:
            "bg-gradient-to-b from-[#FECACA] via-[#EF4444] to-[#991B1B] text-[#3F0808] shadow-[inset_0_1px_0_rgba(255,210,210,0.7),inset_0_-1px_0_rgba(120,25,25,0.45),0_1px_2px_rgba(0,0,0,0.35)]",
        }
      : isBigWin
        ? {
            label: "BIG WIN",
            // Canonical gold recipe via the shared utility (see button-gold.css)
            // so this badge never drifts from the gold button / header avatar.
            className: "roost-tactile-gold text-primary-foreground",
          }
        : {
            label: "WIN",
            className:
              "bg-gradient-to-b from-[#A7F3D0] via-[#10B981] to-[#065F46] text-[#053723] shadow-[inset_0_1px_0_rgba(190,255,220,0.7),inset_0_-1px_0_rgba(10,80,55,0.45),0_1px_2px_rgba(0,0,0,0.35)]",
          };

  const currencyIcon =
    currency === "gold"
      ? "/assets/coins/Gold-Coin.png"
      : "/assets/coins/Cluck-Coin.png";

  // Win amount uses a currency-tinted colour; loss is always red. Both rely
  // on the +/- prefix and the coin icon to carry meaning, so the colour is
  // an enhancement rather than the only signal.
  const amountColor = !isWin
    ? "text-red-400"
    : currency === "gold"
      ? "text-primary"
      : "text-emerald-400";

  const formatRelativeTime = (ts) => {
    if (!ts) return "";
    const date = ts instanceof Date ? ts : new Date(ts);
    if (Number.isNaN(date.getTime())) return "";
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 5) return "just now";
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
  };

  // Outcome-specific background image. The dark scrim sits above the artwork
  // so white text + coloured pills stay legible on whatever the artwork is.
  const backgroundImage = !isWin
    ? CARD_BACKGROUNDS.loss
    : isBigWin
      ? CARD_BACKGROUNDS.bigWin
      : CARD_BACKGROUNDS.win;

  // Card style. BIG WIN cards use a three-layer background: scrim + artwork
  // clipped to padding-box, plus a gold gradient clipped to border-box. The
  // border itself is transparent so the gradient shows through, producing
  // a polished gradient gold border instead of a flat-colour rule.
  //
  // YOU rows take precedence over BIG WIN's gold border treatment: when
  // isMe is true we keep the (BIG WIN) artwork background but swap the
  // gold gradient border for a flat 1px orange border (#F97316) — no
  // gold halo, no glow. The user's own win is visually owned by orange.
  const cardStyle = isMe
    ? backgroundImage
      ? {
          backgroundImage: `linear-gradient(rgba(24, 24, 27, 0.55), rgba(24, 24, 27, 0.55)), url('${backgroundImage}')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : undefined
    : isBigWin
      ? {
          backgroundImage: `linear-gradient(rgba(24, 24, 27, 0.55), rgba(24, 24, 27, 0.55)), url('${backgroundImage}'), linear-gradient(135deg, #FFE48A 0%, #F5B955 50%, #A87A1F 100%)`,
          backgroundSize: "cover, cover, auto",
          backgroundPosition: "center, center, 0 0",
          backgroundRepeat: "no-repeat",
          backgroundClip: "padding-box, padding-box, border-box",
          backgroundOrigin: "padding-box, padding-box, border-box",
          borderColor: "transparent",
          borderWidth: "1px",
          borderStyle: "solid",
        }
      : backgroundImage
        ? {
            backgroundImage: `linear-gradient(rgba(24, 24, 27, 0.55), rgba(24, 24, 27, 0.55)), url('${backgroundImage}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }
        : undefined;

  // Outline classes by row kind:
  //   - YOU rows: flat orange hairline, no glow, no halo. Subtle only.
  //   - BIG WIN: subtle gold halo on hover (gradient border in cardStyle).
  //   - Default: zinc hairline that brightens on hover.
  const outlineClass = isMe
    ? "border border-[#F97316] hover:border-[#FB923C]"
    : isBigWin
      ? "hover:shadow-[0_0_22px_rgba(245,185,85,0.35)]"
      : "border border-border/80 hover:border-border";

  const interactiveClass = isInteractive
    ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#18181b]"
    : "";

  // Render as a button when clickable so it's keyboard-accessible.
  const Wrapper = isInteractive ? "button" : "div";

  return (
    <Wrapper
      type={isInteractive ? "button" : undefined}
      onClick={isInteractive ? onClick : undefined}
      className={`block w-full text-left bg-card ${outlineClass} ${interactiveClass} rounded-xl p-4 transition-all duration-200 ${className}`}
      style={cardStyle}
    >
      <div className="flex items-start gap-3">
        {/* Game thumbnail. Falls back to a skeleton while loading and
            stays on the skeleton if the image errors out, so a missing
            asset never shows the broken-image icon. */}
        <Thumbnail
          src={game.thumbnail || game.image}
          alt={game.name}
          isBigWin={isBigWin}
        />

        {/* Right column — title, name + time, amount */}
        <div className="flex-1 min-w-0">
          {/* Game title + outcome badge */}
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-white font-bold text-base leading-tight truncate min-w-0">
              {game.name}
            </p>
            <span
              className={`flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${badge.className}`}
            >
              {badge.label}
            </span>
          </div>

          {/* Name and time on the same row, separated by a faint vertical
              bar — matches the reference layout. YOU rows omit the
              username (the YOU badge above already identifies the player
              and the /activity/me/recent endpoint never returns a name). */}
          <div className="mt-1 flex items-center gap-2 min-w-0">
            {!isMe && (
              <>
                <span className="text-foreground text-sm truncate min-w-0">
                  {maskUsername(user?.username || user?.name)}
                </span>
                <span
                  className="text-muted-foreground text-sm flex-shrink-0 select-none"
                  aria-hidden="true"
                >
                  |
                </span>
              </>
            )}
            <span className="text-muted-foreground text-xs flex-shrink-0">
              {formatRelativeTime(timestamp)}
            </span>
          </div>

          {/* Amount + coin — right-aligned visual climax */}
          <div className="mt-3 flex items-center justify-end gap-2">
            <img
              src={currencyIcon}
              alt=""
              aria-hidden="true"
              className="w-6 h-6 flex-shrink-0"
            />
            <span className={`text-2xl font-bold leading-none ${amountColor}`}>
              {isWin ? "+" : "-"}
              {formatAmount(amount)}
            </span>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default SocialProofCard;
